import {
  retrieveFrontalFacialImageValidation,
  retrieveProfileFacialImageValidation,
} from "@orval/ageless-imaging/ageless-imaging";
import type {
  DisplayFrontalFacialImageValidation,
  DisplayProfileFacialImageValidation,
} from "@orval/model";

export const PHOTO_UPLOAD_VALIDATION_MAX_ATTEMPTS = 10;
export const PHOTO_UPLOAD_VALIDATION_POLL_INTERVAL_MS = 1_000;

type PhotoUploadValidationGuardArgs = {
  frontImageId: string;
  profileImageId: string;
  requestOptions?: RequestInit;
  maxAttempts?: number;
  pollIntervalMs?: number;
};

export type PhotoUploadValidationGuardResult = {
  frontValidation: DisplayFrontalFacialImageValidation;
  profileValidation: DisplayProfileFacialImageValidation;
  attemptsUsed: number;
};

function isValidationResolved(validation: {
  isValidated?: boolean | null;
  isUsable?: boolean | null;
}): boolean {
  return typeof validation.isUsable === "boolean";
}

function describeValidationState(
  imageType: "front" | "profile",
  validation: {
    isValidated?: boolean | null;
    isUsable?: boolean | null;
  },
): string {
  return `${imageType}(isValidated=${String(validation.isValidated)}, isUsable=${String(validation.isUsable)})`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

type WaitForFrontalFacialImageValidationArgs = {
  imageId: string;
  requestOptions?: RequestInit;
  maxAttempts?: number;
  pollIntervalMs?: number;
};

export async function waitForFrontalFacialImageValidation({
  imageId,
  requestOptions,
  maxAttempts = PHOTO_UPLOAD_VALIDATION_MAX_ATTEMPTS,
  pollIntervalMs = PHOTO_UPLOAD_VALIDATION_POLL_INTERVAL_MS,
}: WaitForFrontalFacialImageValidationArgs): Promise<DisplayFrontalFacialImageValidation> {
  if (!imageId) {
    throw new Error("Frontal facial image validation requires an image ID.");
  }
  if (maxAttempts < 1) {
    throw new Error("Frontal facial image validation requires maxAttempts to be at least 1.");
  }
  if (pollIntervalMs < 0) {
    throw new Error("Frontal facial image validation requires pollIntervalMs to be non-negative.");
  }

  let lastValidation: DisplayFrontalFacialImageValidation | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const validation = await retrieveFrontalFacialImageValidation(imageId, requestOptions);
    lastValidation = validation;

    if (isValidationResolved(validation)) {
      return validation;
    }

    if (attempt < maxAttempts && pollIntervalMs > 0) {
      await sleep(pollIntervalMs);
    }
  }

  throw new Error(
    `Frontal facial image validation did not resolve after ${maxAttempts} attempts. ${describeValidationState(
      "front",
      lastValidation ?? { isValidated: null, isUsable: null },
    )}.`,
  );
}

export async function guardPhotoUploadValidationBeforeConfirm({
  frontImageId,
  profileImageId,
  requestOptions,
  maxAttempts = PHOTO_UPLOAD_VALIDATION_MAX_ATTEMPTS,
  pollIntervalMs = PHOTO_UPLOAD_VALIDATION_POLL_INTERVAL_MS,
}: PhotoUploadValidationGuardArgs): Promise<PhotoUploadValidationGuardResult> {
  if (!frontImageId) {
    throw new Error("Photo upload validation guard requires a front image ID.");
  }
  if (!profileImageId) {
    throw new Error("Photo upload validation guard requires a profile image ID.");
  }
  if (maxAttempts < 1) {
    throw new Error("Photo upload validation guard requires maxAttempts to be at least 1.");
  }
  if (pollIntervalMs < 0) {
    throw new Error("Photo upload validation guard requires pollIntervalMs to be non-negative.");
  }

  let lastFrontValidation: DisplayFrontalFacialImageValidation | null = null;
  let lastProfileValidation: DisplayProfileFacialImageValidation | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const [frontValidation, profileValidation] = await Promise.all([
      retrieveFrontalFacialImageValidation(frontImageId, requestOptions),
      retrieveProfileFacialImageValidation(profileImageId, requestOptions),
    ]);
    lastFrontValidation = frontValidation;
    lastProfileValidation = profileValidation;

    const isFrontResolved = isValidationResolved(frontValidation);
    const isProfileResolved = isValidationResolved(profileValidation);

    if (isFrontResolved && isProfileResolved) {
      return {
        frontValidation,
        profileValidation,
        attemptsUsed: attempt,
      };
    }

    if (attempt < maxAttempts && pollIntervalMs > 0) {
      await sleep(pollIntervalMs);
    }
  }
  if (!lastFrontValidation || !lastProfileValidation) {
    throw new Error(
      "Photo upload validation guard exhausted attempts without any validation result.",
    );
  }

  throw new Error(
    `Photo upload validation did not resolve after ${maxAttempts} attempts. ${describeValidationState(
      "front",
      lastFrontValidation,
    )}; ${describeValidationState("profile", lastProfileValidation)}.`,
  );
}
